import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    AuthStorageMode,
    AuthTokenStorageService,
    PortariaTokenResponse,
    StoredAuthToken,
} from './auth-token-storage.service';

@Injectable({
    providedIn: 'root',
})
export class AuthBootstrapService {
    private readonly authBaseUrl = environment.production
        ? 'https://api.portaria.smosc.com.br'
        : 'https://api.portaria.localhost.smosc.com.br';

    private readonly tokenByCodeEndpoint = `${this.authBaseUrl}/autenticacao/token`;
    private readonly tokenRefreshEndpoint = `${this.authBaseUrl}/autenticacao/token/atualizacao`;

    constructor(
        private readonly http: HttpClient,
        private readonly authStorage: AuthTokenStorageService,
    ) { }

    initialize(): Promise<void> {
        return firstValueFrom(this.initialize$());
    }

    private initialize$(): Observable<void> {
        const { code, storageMode } = this.extractAndSanitizeUrlParams();

        if (code) {
            return this.requestTokenByCode(code, storageMode).pipe(
                tap((tokenResponse) => this.authStorage.saveToken(tokenResponse, storageMode)),
                map(() => void 0),
                catchError((e) => {
                    this.authStorage.markAnonymous();
                    return of(void 0);
                }),
            );
        }

        const storedToken = this.authStorage.readStoredToken();
        if (!storedToken) {
            this.authStorage.markAnonymous();
            return of(void 0);
        }

        return this.refreshToken(storedToken).pipe(
            tap((tokenResponse) =>
                this.authStorage.saveToken(tokenResponse, storedToken.storageMode),
            ),
            map(() => void 0),
            catchError(() => {
                this.authStorage.markAnonymous();
                return of(void 0);
            }),
        );
    }

    private requestTokenByCode(
        code: string,
        storageMode: AuthStorageMode,
    ): Observable<PortariaTokenResponse> {
        const endpoint = `${this.tokenByCodeEndpoint}/${encodeURIComponent(code)}`;

        return this.http.get<PortariaTokenResponse>(endpoint);
    }

    private refreshToken(storedToken: StoredAuthToken): Observable<PortariaTokenResponse> {
        return this.http.put<PortariaTokenResponse>(
            this.tokenRefreshEndpoint,
            {
                token: storedToken.token,
                tokenAtualizacao: storedToken.refreshToken,
                expiraEm: storedToken.expiresAt,
                tempoAuxiliar: storedToken.auxiliaryTime,
            },
            {
                headers: {
                    ignoreLoadingBar: '',
                },
            },
        );
    }

    private extractAndSanitizeUrlParams(): {
        code: string | null;
        storageMode: AuthStorageMode;
    } {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('cd');
        const storageType = url.searchParams.get('s');
        const storageMode: AuthStorageMode = storageType === '1' ? 'local' : 'session';

        if (url.searchParams.has('cd') || url.searchParams.has('s')) {
            url.searchParams.delete('cd');
            url.searchParams.delete('s');

            const cleanedUrl = `${url.pathname}${url.search}${url.hash}`;
            window.history.replaceState({}, document.title, cleanedUrl);
        }

        return { code, storageMode };
    }
}

export function initializeAuthenticationAtBootstrap(
    authBootstrapService: AuthBootstrapService,
): () => Promise<void> {
    return () => authBootstrapService.initialize();
}
